<template>
  <div class="page">
    <b-card>
      <div class="d-flex justify-content-between gap-2 mb-3">
        <h1>Edit Admin</h1>
      </div>

      <b-form @submit="updateAdmin">
        <b-form-group
          id="name"
          label="Name:"
          label-for="name"
          label-cols-md="3"
          label-align-sm="right"
          :state="errors.name ? false : null"
          :invalid-feedback="errors.name"
        >
          <b-form-input
            id="name"
            v-model="name"
            type="text"
            placeholder="Enter name"
          ></b-form-input>
        </b-form-group>

        <b-form-group
          id="email"
          label="Email:"
          label-for="email"
          label-cols-md="3"
          label-align-sm="right"
          :state="errors.email ? false : null"
          :invalid-feedback="errors.email"
        >
          <b-form-input
            id="email"
            v-model="email"
            type="email"
            placeholder="Enter email"
          ></b-form-input>
        </b-form-group>

        <b-form-group
          id="password"
          label="Password:"
          label-for="password"
          label-cols-md="3"
          label-align-sm="right"
          :state="errors.password ? false : null"
          :invalid-feedback="errors.password"
        >
          <b-form-input
            id="password"
            v-model="password"
            type="password"
            placeholder="Enter password"
          ></b-form-input>
        </b-form-group>

        <b-form-group
          id="role"
          label="Role:"
          label-for="role"
          label-cols-md="3"
          label-align-sm="right"
          :state="errors.role ? false : null"
          :invalid-feedback="errors.role"
        >
          <b-form-select
            id="role"
            v-model="role"
            :options="roles"
          ></b-form-select>
        </b-form-group>

        <b-button type="submit" variant="primary">Update Admin</b-button>
      </b-form>
    </b-card>
  </div>
</template>

<script>
import AdminService from "@/core/services/admin.service";
import { yupToKV } from "../../../core/utils/yupToKV";
import { object, string } from "yup";
import { SET_BREADCRUMB } from "../../../core/services/store/breadcrumbs.module";
import { VERIFY_AUTH } from "../../../core/services/store/auth.module";

export default {
  data() {
    return {
      name: "",
      email: "",
      password: "",
      role: "Admin",
      roles: [
        { value: "Admin", text: "Admin" },
        { value: "SuperAdmin", text: "Super Admin" },
      ],
      errors: {},
    };
  },
  computed: {
    adminId() {
      return this.$route.params.id;
    },
    validationSchema() {
      return object().shape({
        name: string()
          .trim()
          .min(2, "Your name must be at least 2 characters long.")
          .max(100, "Your name must be no longer than 100 characters.")
          .required("Name is required"),
        email: string()
          .trim()
          .email("Invalid email")
          .required("Email is required"),
        password: string()
          .trim()
          .test(
            "is-empty-or-valid-password",
            "Your password must be at least 6 characters long.",
            (value) => !value || value.length >= 6
          )
          .test(
            "is-empty-or-valid-password",
            "Your password must be no longer than 100 characters.",
            (value) => !value || value.length <= 100
          )
          .test(
            "is-empty-or-valid-password",
            "Your password must contain at least one uppercase letter.",
            (value) => !value || /[A-Z]/.test(value)
          )
          .test(
            "is-empty-or-valid-password",
            "Your password must contain at least one lowercase letter.",
            (value) => !value || /[a-z]/.test(value)
          )
          .test(
            "is-empty-or-valid-password",
            "Your password must contain at least one number.",
            (value) => !value || /[0-9]/.test(value)
          )
          .test(
            "is-empty-or-valid-password",
            "Your password must contain at least one special character.",
            (value) => !value || /[\^$*.[\]{}()?"!@#%&/,><':;|_~`]/.test(value)
          ),
        role: string().trim().required("Role is required"),
      });
    },
  },
  methods: {
    async getAdmin() {
      try {
        const { data } = await AdminService.getAdmin(this.adminId);
        this.name = data.admin.name;
        this.email = data.admin.email;
        this.role = data.admin.role;
        this.$store.dispatch(SET_BREADCRUMB, [
          { title: "Admins", route: "/admins" },
          {
            title: `Edit Admin ${this.name}`,
            route: `/admins/edit/${data.admin.id}`,
          },
        ]);
      } catch (error) {
        this.$toast(error.response.data.message, { type: "error" });
      } finally {
      }
    },
    async updateAdmin(e) {
      e.preventDefault();
      this.errors = {};
      try {
        const admin = {
          name: this.name,
          email: this.email,
          password: this.password,
          role: this.role,
        };
        await this.validationSchema
          .validate(admin, { abortEarly: false })
          .catch((err) => {
            this.errors = yupToKV(err);
          });

        if (this.validationSchema.isValidSync(admin)) {
          await AdminService.updateAdmin(this.adminId, admin); // Call the updateAdmin method instead of createAdmin
          this.$store.dispatch(VERIFY_AUTH);
          this.$toast("Admin updated successfully");
          this.$router.push({ name: "all-admins" });
        }
      } catch (error) {
        this.errors = error.response.data.error;
        this.$toast(error.response.data.message, { type: "error" });
      } finally {
      }
    },
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: "Admins", route: "/admins" },
      { title: "Edit Admin", route: "/admins/edit" },
    ]);
    this.getAdmin();
  },
};
</script>
